import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { APP_NAME, LOGO_SRC } from "@/lib/brand";
import { NotificationBell } from "@/components/notification-bell";
import { useQuietToolbar } from "@/lib/quiet-toolbar";
import { TeachIntro } from "@/teach/teach-card";
import { TaskInboxButton } from "@/components/task-inbox-button";
import { RefreshButton } from "@/components/refresh-button";
import { ThemeToggle } from "@/components/theme-toggle";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ImpersonationBanner } from "@/components/impersonation";
import { FlagProblemButton } from "@/pages/field/flag-problem";
import {
    ArrowLeft,
    CalendarDays,
    ChevronDown,
    History,
    LogOut,
    Settings,
    MessageSquare,
    IdCard,
    Sparkles,
    BellOff,
} from "lucide-react";
import { WhatsNewDialog, useWhatsNewAvailable } from "@/teach/whats-new";

/**
 * The job wizards own their own step-by-step navigation and carry unsaved draft
 * state, so they do not get the chrome-level Back button — a tech mid-test
 * should not be handed a one-tap bail-out he did not mean to press. Everywhere
 * else below /field, the Back button is the guaranteed way home.
 */
const WIZARD_PREFIXES = ["/field/backflow/", "/field/fire-safety/", "/field/work-order/", "/field/work-order-pdf/"];

/**
 * Whether the chrome draws its own way home for this route: anywhere under
 * /field except the field home itself (already home) and the job wizards.
 */
export function shouldShowFieldBack(pathname: string): boolean {
    if (!pathname.startsWith("/field")) return false;
    if (pathname === "/field" || pathname === "/field/") return false;
    return !WIZARD_PREFIXES.some((w) => pathname.startsWith(w));
}

/** The iPad home screen — the only place the tech top bar shows Help Desk. */
export function isFieldHome(pathname: string): boolean {
    return pathname === "/field" || pathname === "/field/";
}

/**
 * Stripped-down top bar for field technicians.
 * No nav pills — the field page owns its own Today/Upcoming/Past tabs and
 * a job-detail screen fully takes over. The top bar only carries identity
 * + notifications + theme + account, leaving max screen real estate for work.
 */
export function FieldTopBar() {
    const { user, logout } = useAuth();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // #1305 — the only way off a field sub-page used to be whatever back link
    // that page drew for itself, and /field/time-off drew its one in the on-card
    // text color, invisible against the shell. A page's styling should never be
    // the thing standing between a tech and the way home, so the chrome carries
    // an exit of its own, on a surface whose contrast is guaranteed.
    const showBack = shouldShowFieldBack(pathname);
    // Teach cards (#1838): What's new → Show me again.
    const whatsNewOn = useWhatsNewAvailable();
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    // The Quiet Toolbar (10/10): the bell comes off the tech's bar too. Their
    // marks sit on the job cards; urgent-task and call-in pushes still reach
    // the phone. History is Recent activity in this menu.
    const [quiet, setQuiet] = useQuietToolbar();

    const unread = useQuery({
        queryKey: ["chat", "unread-total"],
        queryFn: () => api.get<{ total: number }>("/api/chat/unread-total"),
        refetchInterval: 30000,
        enabled: !!user,
    });
    const unreadCount = unread.data?.total ?? 0;

    return (
        <>
            <header className="sticky top-0 z-40 flex items-center h-14 border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950 px-3 gap-1 shadow-sm">
                <Link to="/field" className="flex items-center gap-2 shrink-0 mr-3">
                    <img
                        src={LOGO_SRC}
                        alt=""
                        className="h-8 w-8 rounded-md object-cover border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                    />
                    <span className="font-bold text-sm tracking-tight text-gray-900 dark:text-white">{APP_NAME}</span>
                </Link>

                {showBack && (
                    <button
                        type="button"
                        onClick={() => navigate("/field")}
                        className="flex items-center gap-1.5 rounded-lg px-2.5 h-9 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Back</span>
                    </button>
                )}

                <div className="flex-1" />

                <div className="flex items-center gap-1 shrink-0">
                    {/* The tech's Help Desk on the iPad home screen (#1838): the same
                        one-tap Flag a problem the job screens carry, for an
                        app-wide gripe. The teach spotlight points here. */}
                    {user && isFieldHome(pathname) && (
                        <FlagProblemButton
                            jobId={null}
                            label="Help Desk"
                            teach
                            // iPad and up: a phone's bar is already full (the spotlight
                            // skips a button it can't see).
                            className="hidden text-gray-700 hover:text-gray-900 sm:inline-flex dark:text-gray-200 dark:hover:text-white"
                        />
                    )}
                    {user && (
                        <NavLink
                            to="/field/chat"
                            className="relative flex items-center justify-center h-9 w-9 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <MessageSquare className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground px-1">
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                        </NavLink>
                    )}
                    {user && <TaskInboxButton />}
                    {user && !quiet && <NotificationBell />}
                    <RefreshButton />
                    <ThemeToggle />
                    {user && (
                        <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="flex items-center gap-1.5 rounded-lg px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary dark:bg-primary/15 dark:text-primary text-sm font-semibold">
                                        {user.display_name?.charAt(0)?.toUpperCase() ?? "U"}
                                    </div>
                                    <ChevronDown className="h-3 w-3 text-gray-400 hidden sm:block" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent side="bottom" align="end" sideOffset={4} className="w-56 p-1.5">
                                <div className="px-2.5 py-2 border-b mb-1">
                                    <p className="text-sm font-medium truncate">{user.display_name}</p>
                                    <p className="text-xs text-foreground/80 capitalize">{user.role}</p>
                                </div>
                                <NavLink
                                    to="/field/profile"
                                    onClick={() => setUserMenuOpen(false)}
                                    className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-secondary transition-colors"
                                >
                                    <IdCard className="h-3.5 w-3.5 text-foreground/80" />
                                    <span>My credentials</span>
                                </NavLink>
                                <NavLink
                                    to="/field/time-off"
                                    onClick={() => setUserMenuOpen(false)}
                                    className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-secondary transition-colors"
                                >
                                    <CalendarDays className="h-3.5 w-3.5 text-foreground/80" />
                                    <span>Time off</span>
                                </NavLink>
                                <NavLink
                                    to="/settings"
                                    onClick={() => setUserMenuOpen(false)}
                                    className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-secondary transition-colors"
                                >
                                    <Settings className="h-3.5 w-3.5 text-foreground/80" />
                                    <span>Settings</span>
                                </NavLink>
                                {quiet && (
                                    <NavLink
                                        to="/notifications"
                                        onClick={() => setUserMenuOpen(false)}
                                        data-testid="field-recent-activity-link"
                                        className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-secondary transition-colors"
                                    >
                                        <History className="h-3.5 w-3.5 text-foreground/80" />
                                        <span>Recent activity</span>
                                    </NavLink>
                                )}
                                <button
                                    type="button"
                                    data-testid="field-quiet-toolbar-switch"
                                    aria-pressed={quiet}
                                    onClick={() => {
                                        setUserMenuOpen(false);
                                        setQuiet(!quiet);
                                    }}
                                    className="flex items-center gap-2.5 w-full rounded-md px-2.5 py-1.5 text-sm hover:bg-secondary transition-colors"
                                >
                                    <BellOff className="h-3.5 w-3.5 text-foreground/80" />
                                    <span>{quiet ? "Bring the bell back" : "Try the quiet toolbar"}</span>
                                </button>
                                {whatsNewOn && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setUserMenuOpen(false);
                                            setWhatsNewOpen(true);
                                        }}
                                        className="flex items-center gap-2.5 w-full rounded-md px-2.5 py-1.5 text-sm hover:bg-secondary transition-colors"
                                    >
                                        <Sparkles className="h-3.5 w-3.5 text-foreground/80" />
                                        <span>What&apos;s new</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUserMenuOpen(false);
                                        logout();
                                    }}
                                    className="flex items-center gap-2.5 w-full rounded-md px-2.5 py-1.5 text-sm hover:bg-destructive/10 text-destructive transition-colors"
                                >
                                    <LogOut className="h-3.5 w-3.5" />
                                    <span>Sign out</span>
                                </button>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </header>
            <ImpersonationBanner headerOffset="3.5rem" />
            <WhatsNewDialog open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
            <TeachIntro featureKey="quiet.toolbar" when={quiet} />
        </>
    );
}
